import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getTableCellErrorState } from '@/components/ui-v2/inputs/table/hooks/get-table-cell-error-state';
import { TablePercentageDisplay } from '@/components/ui-v2/inputs/table/table-percentage-display';
import { TablePercentageInput } from '@/components/ui-v2/inputs/table/table-percentage-input';
import { useIsCellEditing } from '@/hooks/use-is-cell-editing';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import {
  DealItem,
  DealItemWithVersions,
  PricingMode,
} from '@/lib/graphql/generated/graphql';
import { formatDecimal } from '@/lib/utils';
import { useSetColumnSort } from '@/states/column-sort.state';
import { pricingModeState } from '@/states/pricing-mode.state';
import { Row } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

interface ColumnProps {
  handleCellClick: (id: string, field: keyof DealItem) => void;
  handleCellEdit: <K extends keyof DealItem>(
    id: string | undefined,
    field: K,
    value: DealItem[K],
    isLeafRow?: boolean
  ) => Promise<void>;
  handleKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  handleBlur: React.FocusEventHandler<HTMLInputElement>;
}

interface MarginPercentageCellContentProps {
  row: Row<DealItemWithVersions>;
  handleCellClick: ColumnProps['handleCellClick'];
  handleCellEdit: ColumnProps['handleCellEdit'];
  handleKeyDown: ColumnProps['handleKeyDown'];
  handleBlur: ColumnProps['handleBlur'];
}

function MarginPercentageCellContent({
  row,
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
  handleBlur,
}: MarginPercentageCellContentProps) {
  const isOfferLocked = useIsOfferLocked();
  const pricingMode = useAtomValue(pricingModeState);
  const isEditing = useIsCellEditing(row, 'marginPercentage');
  const isParent = row.subRows.length > 1;

  const isMarginEditable =
    !isOfferLocked && pricingMode === PricingMode.MarginFixed;

  const getValue = () => {
    const value = row.original.activeVersion.marginPercentage;
    if (typeof value === 'undefined' || value === null) return null;
    return value;
  };

  const getParentValue = () => {
    const subRowsValues: number[] = [];
    row.subRows.forEach((subRow) => {
      const value = subRow.original.activeVersion.marginPercentage;
      if (typeof value !== 'undefined' && value !== null) {
        subRowsValues.push(value);
      }
    });
    if (subRowsValues.length === 0) return null;
    const min = Math.min(...subRowsValues);
    const max = Math.max(...subRowsValues);
    return min === max
      ? min
      : `${formatDecimal(min)}% - ${formatDecimal(max)}%`;
  };

  const errorState = getTableCellErrorState({
    row,
    field: 'marginPercentage',
    errorType: 'empty',
  });

  const displayValue = isParent ? getParentValue() : getValue();
  const formattedValue =
    typeof displayValue === 'number'
      ? `${formatDecimal(displayValue)}%`
      : typeof displayValue === 'string'
        ? displayValue
        : '—';

  return (
    <TableCellWithRecentlyModified
      productId={row.original.id}
      field="marginPercentage"
      isParent={isParent}
    >
      {(isRecentlyModified) => (
        <div className="h-full w-full">
          {isEditing ? (
            <TablePercentageInput
              value={row.original.activeVersion.marginPercentage ?? null}
              onChange={async (value) => {
                await handleCellEdit(
                  row.original.activeVersion.id,
                  'marginPercentage',
                  value,
                  !isParent
                );
              }}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              disabled={!isMarginEditable}
              autoFocus={isEditing}
              hasError={errorState.hasError}
            />
          ) : (
            <TablePercentageDisplay
              value={formattedValue}
              isEmpty={errorState.isEmpty}
              hasError={errorState.hasError}
              errorMessage={errorState.errorMessage}
              isEditable={isMarginEditable}
              isParent={isParent}
              isRecentlyModified={isRecentlyModified}
              onClick={() =>
                isMarginEditable &&
                !isParent &&
                handleCellClick(
                  row.original.activeVersion.id,
                  'marginPercentage'
                )
              }
            />
          )}
        </div>
      )}
    </TableCellWithRecentlyModified>
  );
}

const useColumnDefMarginPercentage = ({
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
  handleBlur,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const { setColumnSort } = useSetColumnSort();

  const accessorKey: keyof DealItem = 'marginPercentage';

  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'marginPercentage',
      accessorFn: (row) => row.activeVersion?.marginPercentage ?? 0,
      aggregationFn: 'mean',
      sortingFn: 'basic',
      header: () => (
        <div className="cursor-pointer font-semibold">
          <div
            className="flex items-center"
            onClick={() => setColumnSort(accessorKey)}
          >
            {t('kpi.margin_percentage')} <SortIcon pointer="marginPercentage" />
          </div>
        </div>
      ),
      cell: ({ row }) => (
        <MarginPercentageCellContent
          row={row}
          handleCellClick={handleCellClick}
          handleCellEdit={handleCellEdit}
          handleKeyDown={handleKeyDown}
          handleBlur={handleBlur}
        />
      ),
      size: 180,
    }),
    [
      t,
      setColumnSort,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
      handleBlur,
    ]
  );
};

export default useColumnDefMarginPercentage;
